/** Gem shape drawing, ported from the prototype (visual parity intended). */
import type { GemShape } from '../data/balance.js';

export function shade(hex: string, amt: number): string {
  const n = parseInt(hex.slice(1), 16);
  const r = Math.max(0, Math.min(255, (n >> 16) + amt));
  const g = Math.max(0, Math.min(255, ((n >> 8) & 255) + amt));
  const b = Math.max(0, Math.min(255, (n & 255) + amt));
  return `rgb(${r},${g},${b})`;
}

export type DrawableShape = GemShape | 'star';

export function drawShape(
  ctx: CanvasRenderingContext2D,
  shape: DrawableShape,
  cx: number,
  cy: number,
  s: number,
): void {
  ctx.beginPath();
  switch (shape) {
    case 'diamond':
      ctx.moveTo(cx, cy - s);
      ctx.lineTo(cx + s, cy);
      ctx.lineTo(cx, cy + s);
      ctx.lineTo(cx - s, cy);
      break;
    case 'hex':
      for (let i = 0; i < 6; i++) {
        const a = Math.PI / 6 + (i * Math.PI) / 3;
        if (i === 0) ctx.moveTo(cx + s * Math.cos(a), cy + s * Math.sin(a));
        else ctx.lineTo(cx + s * Math.cos(a), cy + s * Math.sin(a));
      }
      break;
    case 'octa':
      for (let i = 0; i < 8; i++) {
        const a = Math.PI / 8 + (i * Math.PI) / 4;
        if (i === 0) ctx.moveTo(cx + s * Math.cos(a), cy + s * Math.sin(a));
        else ctx.lineTo(cx + s * Math.cos(a), cy + s * Math.sin(a));
      }
      break;
    case 'tri':
      ctx.moveTo(cx, cy - s);
      ctx.lineTo(cx + s * 0.95, cy + s * 0.7);
      ctx.lineTo(cx - s * 0.95, cy + s * 0.7);
      break;
    case 'tridown':
      ctx.moveTo(cx, cy + s);
      ctx.lineTo(cx + s * 0.95, cy - s * 0.7);
      ctx.lineTo(cx - s * 0.95, cy - s * 0.7);
      break;
    case 'oval':
      ctx.ellipse(cx, cy, s * 0.65, s, 0, 0, 7);
      break;
    case 'circle':
      ctx.arc(cx, cy, s * 0.85, 0, 7);
      break;
    case 'spark':
      for (let i = 0; i < 8; i++) {
        const r = i % 2 === 0 ? s : s * 0.4;
        const a = -Math.PI / 2 + (i * Math.PI) / 4;
        if (i === 0) ctx.moveTo(cx + r * Math.cos(a), cy + r * Math.sin(a));
        else ctx.lineTo(cx + r * Math.cos(a), cy + r * Math.sin(a));
      }
      break;
    case 'star':
      for (let i = 0; i < 10; i++) {
        const r = i % 2 === 0 ? s : s * 0.45;
        const a = -Math.PI / 2 + (i * Math.PI) / 5;
        if (i === 0) ctx.moveTo(cx + r * Math.cos(a), cy + r * Math.sin(a));
        else ctx.lineTo(cx + r * Math.cos(a), cy + r * Math.sin(a));
      }
      break;
  }
  ctx.closePath();
}

export function drawGemIcon(
  ctx: CanvasRenderingContext2D,
  shape: DrawableShape,
  color: string,
  cx: number,
  cy: number,
  s: number,
  glow: boolean,
): void {
  ctx.save();
  if (glow) {
    ctx.shadowColor = color;
    ctx.shadowBlur = 9;
  }
  const grad = ctx.createLinearGradient(cx, cy - s, cx, cy + s);
  grad.addColorStop(0, shade(color, 60));
  grad.addColorStop(0.55, color);
  grad.addColorStop(1, shade(color, -50));
  drawShape(ctx, shape, cx, cy, s);
  ctx.fillStyle = grad;
  ctx.fill();
  ctx.shadowBlur = 0;
  ctx.strokeStyle = 'rgba(0,0,0,0.45)';
  ctx.lineWidth = 1;
  ctx.stroke();
  drawShape(ctx, shape, cx - s * 0.18, cy - s * 0.22, s * 0.42);
  ctx.fillStyle = 'rgba(255,255,255,0.28)';
  ctx.fill();
  ctx.restore();
}
